import * as React from 'react'
import { useEffect, useState } from 'react'
import { Categories } from './results/categoriesResults'
import { queryConfig, SearchResults } from './results/searchResults'
import styles from './styles.css'

import classNames from 'classnames'
import { trackClick, trackView } from './utils/tracking'
import { MagnifyingGlass } from './components/magnifyingGlass'

type PropsType = {
  config_uuid: string
  actionMap: any
  previewMode?: boolean
}

export const useHotkey = (onTrigger: (pressedKEys: any) => void) => {
  const [pressedKeys, setPressedKeys] = React.useState({})

  useEffect(() => {
    const handleTriggerKey = (event: KeyboardEvent) => {
      pressedKeys[event.key] = true
      setPressedKeys(pressedKeys)

      onTrigger(pressedKeys)

      if (event.key === 'Enter') {
        delete pressedKeys[event.key]
        setPressedKeys(pressedKeys)
      }
    }

    const handleKeyupKey = (event: KeyboardEvent) => {
      delete pressedKeys[event.key]
      setPressedKeys(pressedKeys)

      if (event.key === 'Meta' || event.key === 'Enter') {
        setPressedKeys({})
      }

      onTrigger(pressedKeys)
    }

    window.addEventListener('keydown', handleTriggerKey)
    window.addEventListener('keyup', handleKeyupKey)

    return () => {
      window.removeEventListener('keydown', handleTriggerKey)
      window.removeEventListener('keyup', handleKeyupKey)
    }
  }, [onTrigger])
}

export const Keysho = (props: PropsType) => {
  const [CONFIG, setCONFIG] = useState([])
  const [colors, setColors] = useState({
    primary: 'red',
    secondary: 'blue',
    accent: 'purple',
    text: 'black',
    subtext: 'gray',
    border: 'green'
  })
  const [showBranding, setShowBranding] = useState(true)
  const [fetchedConfig, setFetchedConfig] = useState(false)
  const [omniboxActive, setOmniboxActive] = useState(false || props.previewMode)
  const [sectionNumber, setSectionNumber] = useState(0)
  const [activeRowIndex, setActiveRowIndex] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')

  const searchInput: any = React.useRef(null)

  const searchConfig = queryConfig(CONFIG, searchQuery)

  const [resultsMode, setResultsMode] = useState('categories') // categories, search

  if (!fetchedConfig) {
    fetch(`https://keysho.herokuapp.com/configs/retrieve/${props.config_uuid}`)
      .then((j) => j.json())
      .then((res) => {
        setCONFIG(res.entries)
        setColors(res.config?.colors)
        setShowBranding(res.show_branding)

        if (!props.previewMode) trackView(props.config_uuid, 'loaded project')
      })
      .catch(() => {
        setCONFIG([])
      })
    setFetchedConfig(true)
  }

  const searchHandler = (e: any) => {
    const text = e.target.value

    setSearchQuery(text)
    setActiveRowIndex(0)

    if (text.length === 0) {
      setResultsMode('categories')
    } else {
      setResultsMode('search')
    }
  }

  const handleTrigger = (pressedKeys: any) => {
    if (pressedKeys.Escape) {
      closeOmnibox()
    }

    if (pressedKeys.Meta && pressedKeys.k) {
      setResultsMode('categories')
      setActiveRowIndex(0)
      setSectionNumber(0)
      toggleOmnibox()

      if (!omniboxActive) {
        setTimeout(() => searchInput.current.focus(), 0)
      }
    }

    if (pressedKeys.ArrowLeft) {
      previousSection()
    }

    if (pressedKeys.ArrowRight) {
      nextSection()
    }

    if (pressedKeys.ArrowUp) {
      previousActiveRow()
    }

    if (pressedKeys.ArrowDown) {
      nextActiveRow()
    }

    if (pressedKeys.Enter) {
      const entry =
        resultsMode === 'categories'
          ? CONFIG[Object.keys(CONFIG)[sectionNumber]][activeRowIndex]
          : searchConfig[activeRowIndex]

      executeAction(entry, pressedKeys.Meta)
      toggleOmnibox()
    }
  }

  useHotkey((pressedKeys: any) => handleTrigger(pressedKeys))

  const toggleOmnibox = () => {
    setOmniboxActive(!omniboxActive)
  }

  const closeOmnibox = () => {
    setOmniboxActive(false)
  }

  const previousActiveRow = () => {
    setActiveRowIndex(Math.max(0, activeRowIndex - 1))
  }

  const nextActiveRow = () => {
    if (resultsMode === 'categories') {
      setActiveRowIndex(
        Math.min(
          Object.keys(CONFIG[Object.keys(CONFIG)[sectionNumber]]).length - 1,
          activeRowIndex + 1
        )
      )
    } else {
      setActiveRowIndex(Math.min(searchConfig.length - 1, activeRowIndex + 1))
    }
  }

  const executeAction = (entry: any, cmdPressed: boolean = false) => {
    const { link, action, name } = entry

    if (props.previewMode) {
      return
    }

    trackClick(props.config_uuid, name).then(() => {
      if (link) {
        if (cmdPressed) {
          window.open(link, '_blank')
        } else {
          window.open(link, '_self')
        }
      } else if (action) {
        if (Object.keys(props.actionMap).includes(action)) {
          props.actionMap[action]()
        } else {
          alert(`Would perform: ${action}();`)
        }
      }
    })
  }

  const previousSection = () => {
    setActiveRowIndex(0)
    setSectionNumber(Math.max(0, sectionNumber - 1))
  }

  const nextSection = () => {
    setActiveRowIndex(0)
    setSectionNumber(
      Math.min(Object.keys(CONFIG).length - 1, sectionNumber + 1)
    )
  }

  if (Object.keys(CONFIG).length === 0) {
    return null
  }

  return (
    <div
      className={classNames(
        styles.container,
        omniboxActive ? styles.show : styles.hide
      )}
      style={{
        backgroundColor: colors?.primary ? colors.primary : 'white',
        paddingBottom: 15
      }}
    >
      {omniboxActive && (
        <div>
          <div style={{ flexDirection: 'row', display: 'flex' }}>
            <MagnifyingGlass color={colors?.text ? colors.text : '#333'} />
            <input
              ref={searchInput}
              type='text'
              className={styles.searchInput}
              style={{
                borderBottom:
                  'solid 2px ' + (colors?.primary ? colors.primary : 'white'),
                color: colors?.text ? colors.text : 'black'
              }}
              placeholder='Search'
              onChange={searchHandler}
              autoFocus
            />
          </div>
          <br />

          <div>
            {resultsMode === 'search' ? (
              <SearchResults
                config={CONFIG}
                colors={colors}
                sectionNumber={sectionNumber}
                setSection={setSectionNumber}
                activeRowIndex={activeRowIndex}
                setActiveRowIndex={setActiveRowIndex}
                executeAction={executeAction}
                search={searchQuery}
                previewMode={props.previewMode}
              />
            ) : (
              <Categories
                config={CONFIG}
                colors={colors}
                sectionNumber={sectionNumber}
                setSection={setSectionNumber}
                activeRowIndex={activeRowIndex}
                setActiveRowIndex={setActiveRowIndex}
                executeAction={executeAction}
                previewMode={props.previewMode}
              />
            )}{' '}
          </div>
        </div>
      )}
      {showBranding === undefined ||
        (showBranding === true && (
          <p style={{ textAlign: 'left', height: 20 }}>
            <span
              style={{
                border: 'solid 1px ' + (colors?.text ? colors.text : '#333'),
                color: colors?.text ? colors.text : '#333'
              }}
              className={styles.poweredPill}
              onClick={() => {
                window.open(
                  'https://aakashadesara.retool.com/embedded/public/e64f3a61-b7e9-468f-957a-003bfcefc46e#searchTerm=',
                  '_blank'
                )
              }}
            >
              Omnibox powered by Keysho (⌘ k)
            </span>
          </p>
        ))}
    </div>
  )
}
